import { supabase } from './supabase';
import type { Goal, Ladder, LadderStep } from '../types/ladder';

// Define the structure of the imported data
interface ImportGoalData {
  ref: string;
  name: string;
  final_outcome: string;
  primaryLadder: string;
}

interface ImportLadderData {
  ref: string;
  goalRef: string;
  outcome_text: string;
  level_number: number;
  parentStepRef?: string;
  steps: ImportStepData[];
}

interface ImportStepData {
  ref: string;
  step_number: number;
  description: string;
}

// Your Notion data structured for import
const NOTION_DATA = {
  goals: [
    {
      ref: 'G1',
      name: 'Always be training for something this year',
      final_outcome: 'I am consistently training year-round with clear events to prepare for',
      primaryLadder: 'L1'
    },
    {
      ref: 'G2',
      name: 'Be proud of my finances',
      final_outcome: 'I feel proud, clear, and confident about my financial situation',
      primaryLadder: 'L3'
    },
    {
      ref: 'G3',
      name: 'Make $10k per month with my podcast by the end of the year',
      final_outcome: '$10k per month in podcast-driven revenue',
      primaryLadder: 'L5'
    }
  ] as ImportGoalData[],

  ladders: [
    {
      ref: 'L1',
      goalRef: 'G1',
      outcome_text: 'Always be training for something this year',
      level_number: 1,
      steps: [
        { ref: 'S1', step_number: 1, description: 'Q1 fitness event scheduled' },
        { ref: 'S2', step_number: 2, description: 'Q2 fitness event scheduled' },
        { ref: 'S3', step_number: 3, description: 'Q3 fitness event scheduled' },
        { ref: 'S4', step_number: 4, description: 'Q4 fitness event scheduled' },
        { ref: 'S5', step_number: 5, description: 'I am consistently training year-round with clear events to prepare for' }
      ]
    },
    {
      ref: 'L2',
      goalRef: 'G1',
      outcome_text: 'Q1 fitness event scheduled',
      level_number: 2,
      parentStepRef: 'S1',
      steps: [
        { ref: 'S6', step_number: 1, description: 'Specific Q1 event identified and committed to' },
        { ref: 'S7', step_number: 2, description: 'Training plan selected' },
        { ref: 'S8', step_number: 3, description: 'First training actions completed' },
        { ref: 'S9', step_number: 4, description: 'Training baseline established' },
        { ref: 'S10', step_number: 5, description: 'Q1 fitness event is officially scheduled' }
      ]
    },
    {
      ref: 'L3',
      goalRef: 'G2',
      outcome_text: 'Be proud of my finances',
      level_number: 1,
      steps: [
        { ref: 'S11', step_number: 1, description: 'Financial systems in place' },
        { ref: 'S12', step_number: 2, description: 'Payment plans set up where needed' },
        { ref: 'S13', step_number: 3, description: 'CPA hired and actively supporting my finances' },
        { ref: 'S14', step_number: 4, description: 'Prepared and positioned for 2027' },
        { ref: 'S15', step_number: 5, description: 'I feel proud, clear, and confident about my financial situation' }
      ]
    },
    {
      ref: 'L4',
      goalRef: 'G2',
      outcome_text: 'Financial systems in place',
      level_number: 2,
      parentStepRef: 'S11',
      steps: [
        { ref: 'S16', step_number: 1, description: 'Monthly financial schedule set up' },
        { ref: 'S17', step_number: 2, description: 'Finances organized and categorized' },
        { ref: 'S18', step_number: 3, description: 'Financial systems chosen' },
        { ref: 'S19', step_number: 4, description: 'Data entered and up to date' },
        { ref: 'S20', step_number: 5, description: 'Financial systems are fully in place and functioning' }
      ]
    },
    {
      ref: 'L5',
      goalRef: 'G3',
      outcome_text: 'Make $10k per month with my podcast by the end of the year',
      level_number: 1,
      steps: [
        { ref: 'S21', step_number: 1, description: 'Episode 1 is live and publicly accessible' },
        { ref: 'S22', step_number: 2, description: 'Production process confirmed' },
        { ref: 'S23', step_number: 3, description: 'Revenue and growth experiments tested' },
        { ref: 'S24', step_number: 4, description: 'Repeatable monetization established' },
        { ref: 'S25', step_number: 5, description: '$10k per month in podcast-driven revenue' }
      ]
    },
    {
      ref: 'L6',
      goalRef: 'G3',
      outcome_text: 'Episode 1 is live and publicly accessible',
      level_number: 2,
      parentStepRef: 'S21',
      steps: [
        { ref: 'S26', step_number: 1, description: 'Clear audience description and podcast name decided' },
        { ref: 'S27', step_number: 2, description: 'Branding direction finalized' },
        { ref: 'S28', step_number: 3, description: 'Lead magnet created and aligned with the show' },
        { ref: 'S29', step_number: 4, description: 'Email chain connected to the podcast is written and ready' },
        { ref: 'S30', step_number: 5, description: 'Show structure and positioning are clearly defined and committed to' }
      ]
    }
  ] as ImportLadderData[]
};

interface ImportResult {
  success: boolean;
  goalCount: number;
  ladderCount: number;
  stepCount: number;
  error?: string;
}

export async function importNotionData(userId: string): Promise<ImportResult> {
  try {
    console.log('Starting Notion data import for user:', userId);

    // Maps to track reference IDs to actual database IDs
    const goalIdMap = new Map<string, string>();
    const stepIdMap = new Map<string, string>();

    // Step 1: Import Goals
    console.log('Importing goals...');
    for (const goalData of NOTION_DATA.goals) {
      const { data: goal, error } = await supabase
        .from('goals')
        .insert({
          user_id: userId,
          name: goalData.name,
          final_outcome: goalData.final_outcome,
          status: 'active'
        })
        .select()
        .single();

      if (error) throw error;
      if (!goal) throw new Error('Goal insertion failed');

      goalIdMap.set(goalData.ref, goal.id);
      console.log(`Created goal ${goalData.ref}: ${goalData.name}`);
    }

    // Step 2: Import Primary Ladders (level 1) with their steps
    console.log('Importing primary ladders...');
    const primaryLadders = NOTION_DATA.ladders.filter(l => l.level_number === 1);

    for (const ladderData of primaryLadders) {
      const goalId = goalIdMap.get(ladderData.goalRef);
      if (!goalId) throw new Error(`Goal ${ladderData.goalRef} not found`);

      // Create ladder
      const { data: ladder, error: ladderError } = await supabase
        .from('ladders')
        .insert({
          goal_id: goalId,
          outcome_text: ladderData.outcome_text,
          level_number: ladderData.level_number,
          parent_step_id: null,
          status: 'active'
        })
        .select()
        .single();

      if (ladderError) throw ladderError;
      if (!ladder) throw new Error('Ladder insertion failed');

      console.log(`Created ladder ${ladderData.ref} for goal ${ladderData.goalRef}`);

      // Create steps for this ladder
      for (const stepData of ladderData.steps) {
        const { data: step, error: stepError } = await supabase
          .from('ladder_steps')
          .insert({
            ladder_id: ladder.id,
            step_number: stepData.step_number,
            description: stepData.description,
            is_completed: false
          })
          .select()
          .single();

        if (stepError) throw stepError;
        if (!step) throw new Error('Step insertion failed');

        stepIdMap.set(stepData.ref, step.id);
        console.log(`Created step ${stepData.ref}: ${stepData.description.substring(0, 30)}...`);
      }
    }

    // Step 3: Import Nested Ladders (level 2+) with their steps
    console.log('Importing nested ladders...');
    const nestedLadders = NOTION_DATA.ladders.filter(l => l.level_number > 1);

    for (const ladderData of nestedLadders) {
      const goalId = goalIdMap.get(ladderData.goalRef);
      if (!goalId) throw new Error(`Goal ${ladderData.goalRef} not found`);

      const parentStepId = ladderData.parentStepRef
        ? stepIdMap.get(ladderData.parentStepRef)
        : null;

      if (ladderData.parentStepRef && !parentStepId) {
        throw new Error(`Parent step ${ladderData.parentStepRef} not found`);
      }

      // Create nested ladder
      const { data: ladder, error: ladderError } = await supabase
        .from('ladders')
        .insert({
          goal_id: goalId,
          outcome_text: ladderData.outcome_text,
          level_number: ladderData.level_number,
          parent_step_id: parentStepId,
          status: 'active'
        })
        .select()
        .single();

      if (ladderError) throw ladderError;
      if (!ladder) throw new Error('Nested ladder insertion failed');

      console.log(`Created nested ladder ${ladderData.ref} for goal ${ladderData.goalRef}`);

      // Create steps for this nested ladder
      for (const stepData of ladderData.steps) {
        const { data: step, error: stepError } = await supabase
          .from('ladder_steps')
          .insert({
            ladder_id: ladder.id,
            step_number: stepData.step_number,
            description: stepData.description,
            is_completed: false
          })
          .select()
          .single();

        if (stepError) throw stepError;
        if (!step) throw new Error('Step insertion failed');

        stepIdMap.set(stepData.ref, step.id);
        console.log(`Created step ${stepData.ref}: ${stepData.description.substring(0, 30)}...`);
      }
    }

    console.log('Import completed successfully!');

    return {
      success: true,
      goalCount: NOTION_DATA.goals.length,
      ladderCount: NOTION_DATA.ladders.length,
      stepCount: NOTION_DATA.ladders.reduce((sum, l) => sum + l.steps.length, 0)
    };

  } catch (error) {
    console.error('Import failed:', error);
    return {
      success: false,
      goalCount: 0,
      ladderCount: 0,
      stepCount: 0,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

// Function to preview what will be imported
export function getImportPreview() {
  return {
    goals: NOTION_DATA.goals.map(g => ({
      name: g.name,
      final_outcome: g.final_outcome,
      ladderCount: NOTION_DATA.ladders.filter(l => l.goalRef === g.ref).length
    })),
    totalGoals: NOTION_DATA.goals.length,
    totalLadders: NOTION_DATA.ladders.length,
    totalSteps: NOTION_DATA.ladders.reduce((sum, l) => sum + l.steps.length, 0)
  };
}
